import { useEffect, useState } from "react";
import { Download, Link, LoaderCircle, Share2 } from "lucide-react";
import { buildShareCardModel, type ShareCardModel } from "../shared/invitations.ts";
import { api } from "./api.ts";
import { useLocale } from "./i18n.tsx";
import { buildShareData, copyInvitationLink, createShareCard } from "./share-card.ts";

type Invitation = { inviteCode: string; shareUrl: string };

export function ShareInvitationCard({ name }: { name: string }) {
  const { locale, copy } = useLocale();
  const text = copy.content;
  const [model, setModel] = useState<ShareCardModel | null>(null);
  const [image, setImage] = useState<{ blob: Blob; url: string } | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setModel(null);
    setImage(null);
    setError("");
    setStatus("");
    void api<Invitation>(`/api/invitations/me?locale=${encodeURIComponent(locale)}`)
      .then(async ({ inviteCode }) => {
        const nextModel = buildShareCardModel(name, inviteCode, locale);
        const blob = await createShareCard(nextModel);
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setModel(nextModel);
        setImage({ blob, url: objectUrl });
      })
      .catch(() => active && setError(text.shareUnavailable));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [locale, name, text.shareUnavailable]);

  function saveImage() {
    if (!model || !image) return;
    const anchor = document.createElement("a");
    anchor.href = image.url;
    anchor.download = model.filename;
    anchor.click();
    setStatus(text.shareSaved);
  }

  async function copyLink() {
    if (!model) return;
    const copied = await copyInvitationLink(navigator.clipboard, model.shareUrl);
    setStatus(copied ? text.shareCopied : text.shareCopyFailed);
  }

  async function share() {
    if (!model || !image) return;
    setSharing(true);
    setStatus("");
    try {
      const file = new File([image.blob], model.filename, { type: "image/png" });
      const shareData = buildShareData(model, file);
      let canShareFile = false;
      try {
        canShareFile = Boolean(navigator.canShare?.(shareData));
      } catch {
        canShareFile = false;
      }
      if (navigator.share && canShareFile) {
        await navigator.share(shareData);
      } else if (navigator.share) {
        await navigator.share({ title: model.shareTitle, text: model.shareText, url: model.shareUrl });
      } else {
        const copied = await copyInvitationLink(navigator.clipboard, model.shareUrl);
        setStatus(copied ? text.shareCopied : text.shareCopyFailed);
        return;
      }
      setStatus(text.shareComplete);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setStatus(text.shareFailed);
    } finally {
      setSharing(false);
    }
  }

  return (
    <section className="invite-section" aria-labelledby="invite-title">
      <div className="invite-copy">
        <p className="eyebrow">{text.shareEyebrow}</p>
        <h2 id="invite-title">{text.shareTitle}</h2>
        <p>{text.shareLead}</p>
        {model && image ? <>
          <div className="invite-actions">
            <button className="button button-primary" type="button" disabled={sharing} onClick={() => void share()}>
              {sharing ? <LoaderCircle className="spinner" aria-hidden="true" /> : <Share2 aria-hidden="true" />}{sharing ? text.sharing : text.shareAction}
            </button>
            <button className="button button-secondary" type="button" onClick={saveImage}><Download aria-hidden="true" />{text.shareSave}</button>
            <button className="button button-quiet" type="button" onClick={() => void copyLink()}><Link aria-hidden="true" />{text.shareCopy}</button>
          </div>
          <p className="invite-code"><span>{text.shareCode}</span><strong>{model.inviteCode}</strong></p>
          <p className="invite-status" role="status" aria-live="polite">{status}</p>
        </> : error ? <div className="form-message error" role="alert">{error}</div>
          : <div className="loading-row"><LoaderCircle className="spinner" aria-hidden="true" />{text.shareLoading}</div>}
      </div>
      <div className="invite-preview">
        {image ? <img src={image.url} alt={text.shareCardAlt} width="1080" height="1350" />
          : <div className="invite-preview-placeholder" aria-hidden="true"><LoaderCircle className="spinner" /></div>}
      </div>
    </section>
  );
}
